import React, { useState } from 'react';
import { useGlobal, Typography } from '@rcg/ui-react';
import FlightDetailsModal from '../ReviewCard/FlightDetailsModal';
import Button from '../UiComponents/Button';
import TimeDuration, { timeWithLocale } from '../../utils/timeFormats';
import layoverFormats from '../../utils/layoverFormats';
import listsFormats from '../../utils/listsFormats';
import Dictionary from '../../utils/dictionary';
import useDictionary from '../../hooks/useDictionary';
import useStyles from './index.style';

const FlightDetails = ({ route, guestNames }) => {
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const getDictionary = useDictionary(Dictionary);

  const classes = useStyles();

  const [openFlightDetailsModal, setOpenFlightDetailsModal] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);

  const firstSegment = route?.flightSegments[0];
  const lastSegment = route && route.flightSegments[route.flightSegments.length - 1];

  const fullNames = guestNames?.map(
    (guest) => `${guest?.firstName?.toLowerCase()} ${guest?.lastName?.toLowerCase()}`,
  );
  const routeLabel =
    route.routeDirection === 'OUTBOUND'
      ? getDictionary(Dictionary.airDepartingLabel.key)
      : getDictionary(Dictionary.airReturningLabel.key);
  const flightType = route.routeDirection === 'OUTBOUND' ? 'departure' : 'return';
  const routeDate = localizedDayjs(firstSegment.departure.date).format('ddd MMM D[,] YYYY');

  const handleOpenFlightDetailsModal = (info) => () => {
    setSelectedFlight(info);
    setOpenFlightDetailsModal(true);
  };

  const handleCloseFlightDetailsModal = () => {
    setSelectedFlight(null);
    setOpenFlightDetailsModal(false);
  };

  const createOperatingAirlines = (flight) => {
    const segment = flight?.flightSegments[0]?.operatingAirline;
    return {
      name: segment?.name,
      code: segment?.code,
      icon: segment?.icon,
    };
  };

  const createCabin = (flight) => {
    const segment = flight?.flightSegments[0];
    return {
      passengerQty: guestNames?.length,
      cabinClass: {
        code: segment?.cabinClass?.code,
        name: segment?.cabinClass?.name,
      },
    };
  };

  const flightInfo = {
    flight: {
      operatingAirlines: [createOperatingAirlines(route)],
      flightSegments: route?.flightSegments,
      departDate: route?.departDate,
    },
    cabin: createCabin(route),
  };

  const startTime = timeWithLocale(localizedDayjs, firstSegment?.departure?.time);
  const endTime = timeWithLocale(localizedDayjs, lastSegment?.arrival?.time);
  const layoverDays = layoverFormats.layoverDays({
    departure: route?.departDate,
    arrival: route?.arrivalDate,
  });

  const numberOfStops = listsFormats.numberOfStops(route.flightSegments, renderContent);
  const airLines = listsFormats.listOfAirlines([createOperatingAirlines(route)]);
  const passengersLabel =
    guestNames.length > 1
      ? getDictionary(Dictionary.airPassengersLabel.key)
      : getDictionary(Dictionary.airPassengerLabel.key);

  return (
    <div className={classes.flightDetails}>
      <div className="title-container">
        <img src={route.flightIcon} alt="airline icon" />
        <Typography variant="h6">
          {routeLabel} — {routeDate}
        </Typography>
        <Typography variant="h6">
          {getDictionary(Dictionary.airFlightDetailsConfirmationLabel.key)}{' '}
          {route.confirmationNumbers}
        </Typography>
        <Button disableRipple onClick={handleOpenFlightDetailsModal({ ...flightInfo, flightType })}>
          {getDictionary(Dictionary.airFlightDetailsViewDetailsLabel.key)}
        </Button>
      </div>
      <div className="left-block">
        <div className="head-block">
          {startTime} — {endTime}
          <span className="layoverDaysReview">
            &nbsp;
            {layoverDays}
          </span>
        </div>
        <Typography component="p">
          {firstSegment?.departure.airport.code} - {TimeDuration(route?.totalDurationMins)} -{' '}
          {lastSegment?.arrival.airport.code}
        </Typography>
        <div className="details">
          <Typography component="p">
            {numberOfStops} - {airLines} -{' '}
            {renderContent(
              Dictionary[`airClass${firstSegment.cabinClass.code}Name`].key,
              Dictionary[`airClass${firstSegment.cabinClass.code}Name`].defaultValue,
            )}
          </Typography>
        </div>
      </div>
      <div className="right-block">
        <Typography className="head-block">{`${firstSegment.marketingAirline.code} ${firstSegment.number}`}</Typography>
        <Typography>
          {guestNames.length} {passengersLabel}
        </Typography>
        <Typography style={{ textTransform: 'capitalize' }}>{fullNames.join(', ')} </Typography>
      </div>
      <FlightDetailsModal
        openModal={openFlightDetailsModal}
        handleClose={handleCloseFlightDetailsModal}
        flightInfo={selectedFlight}
      />
    </div>
  );
};

export default FlightDetails;
